"use client"

import {
  OUR_SERVICES,
  serviceMenuHref,
} from "@lib/content/services"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { clx } from "@modules/common/components/ui"
import { useId, type KeyboardEvent } from "react"

type ContactMenuProps = {
  menuId: string
  open: boolean
  onOpen: () => void
  onClose: () => void
  onCancelClose: () => void
  onScheduleClose: () => void
  label?: string
}

const triggerClass = (open: boolean) =>
  clx(
    "relative z-[70] py-1 text-[15px] font-medium tracking-normal transition-colors",
    open
      ? "text-vx-accent after:absolute after:inset-x-0 after:-bottom-1 after:h-0.5 after:bg-vx-accent"
      : "text-vx-ink/80 hover:text-vx-ink"
  )

const ContactMenu = ({
  menuId,
  open,
  onOpen,
  onClose,
  onCancelClose,
  onScheduleClose,
  label = "ติดต่อ",
}: ContactMenuProps) => {
  const panelId = useId()

  const onTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (
      event.key === "Enter" ||
      event.key === " " ||
      event.key === "ArrowDown"
    ) {
      event.preventDefault()
      onOpen()
    }
  }

  return (
    <div
      data-menu-id={menuId}
      onMouseEnter={() => {
        onCancelClose()
        onOpen()
      }}
      onMouseLeave={onScheduleClose}
    >
      <button
        type="button"
        className={triggerClass(open)}
        aria-expanded={open}
        aria-controls={panelId}
        aria-haspopup="true"
        data-testid="nav-contact-trigger"
        onClick={() => (open ? onClose() : onOpen())}
        onKeyDown={onTriggerKeyDown}
        onFocus={() => {
          onCancelClose()
          onOpen()
        }}
      >
        {label}
      </button>

      {open && (
        <div
          id={panelId}
          role="region"
          aria-label="ติดต่อ"
          data-testid="contact-menu-panel"
          className="absolute inset-x-0 top-full z-[60] w-full"
          onMouseEnter={onCancelClose}
          onMouseLeave={onScheduleClose}
        >
          <div className="border-b border-vx-line bg-vx-surface shadow-[0_12px_32px_rgba(15,23,42,0.12)]">
            <div className="content-container grid gap-8 py-7 small:grid-cols-2 small:gap-12 small:py-9">
              <div>
                <LocalizedClientLink
                  href="/contact"
                  onClick={onClose}
                  className="group block"
                  data-testid="contact-menu-contact-link"
                >
                  <p className="text-[16px] font-semibold text-vx-ink group-hover:text-vx-accent">
                    ติดต่อเรา
                  </p>
                  <p className="mt-1.5 max-w-sm text-[14px] leading-6 text-vx-muted">
                    ปรึกษาโปรเจกต์ ขอใบเสนอราคา หรือนัดสำรวจหน้างาน
                  </p>
                </LocalizedClientLink>
              </div>

              <div>
                <p className="text-[16px] font-semibold text-vx-ink">
                  บริการของเรา
                </p>
                <p className="mt-1.5 text-[14px] leading-6 text-vx-muted">
                  บริการติดตั้งและดูแลระบบเครือข่ายโดยทีม VX Work
                </p>
                <ul className="mt-4 flex flex-col gap-2.5">
                  {OUR_SERVICES.map((service) => (
                    <li key={service.slug}>
                      <LocalizedClientLink
                        href={serviceMenuHref(service)}
                        onClick={onClose}
                        className="text-[14px] text-vx-ink/70 transition-colors hover:text-vx-accent"
                        data-testid={`contact-menu-service-${service.slug}`}
                      >
                        {service.label}
                      </LocalizedClientLink>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ContactMenu
